import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useProgressAnimation } from '../../hooks/useProgressAnimation';
import type { SSETaskStatus } from '../../hooks/useProgressAnimation';


interface MenuPullingLoaderProps {
  isVisible: boolean;
  status?: SSETaskStatus;
}

// Move static arrays outside component for performance
const SKELETON_CATEGORIES = [1, 2, 3, 4];
const SKELETON_ITEMS = [1];

// Width variations for realistic feel
const NAME_WIDTHS = ['w-28', 'w-32', 'w-36'];
const DESC_WIDTHS = ['w-40', 'w-48', 'w-44'];

const MenuPullingLoader: React.FC<MenuPullingLoaderProps> = ({ isVisible, status = 'idle' }) => {
  // Use custom hook for progress animation
  const { progress, statusText } = useProgressAnimation(status);
  const actuallyVisible = isVisible;
  
  // Dynamic title based on status
  const title = status === 'completed' ? 'Menu Ready!' : 'Preparing Your Menu';

  return (
    <AnimatePresence>
      {actuallyVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
          role="status"
          aria-live="polite"
          aria-busy={actuallyVisible}
        >
          {/* Single global shimmer effect */}
          <motion.div
            animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
            className="absolute inset-0 opacity-10"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(238, 68, 35, 0.1), transparent)',
              backgroundSize: '200% 100%',
            }}
          />

          <div className="flex flex-col h-full px-6 max-w-3xl w-full relative z-10">
            {/* Title with Lottie */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center pt-8 mb-6"
            >
              {/* Lottie Animation */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                className="flex justify-center mb-4"
              >
                <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center">
                  <DotLottieReact
                    src="https://lottie.host/6501beaa-826e-4822-b4fa-c68a185c4ca8/iKvNymSb72.lottie"
                    loop
                    autoplay
                    className="w-full h-full"
                  />
                </div>
              </motion.div>

              <h2 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {title}
              </h2>
              <p className="text-gray-400 text-sm md:text-base" style={{ fontFamily: 'Inter, sans-serif' }}>
                We're loading all your items and categories. Almost ready!
              </p>
            </motion.div>

            {/* Skeleton Grid - Realistic Menu Structure */}
            <div className="flex-1 overflow-y-auto mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {SKELETON_CATEGORIES.map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md rounded-xl p-5 border border-white/10 overflow-hidden"
                  >
                    {/* Stripe-style shimmer overlay */}
                    <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
                      <motion.div
                        animate={{ x: ['-100%', '100%'] }}
                        transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"
                      />
                    </div>

                    {/* Category Title */}
                    <div className="h-5 w-40 bg-gray-700/80 rounded mb-4 relative z-10" />

                    {/* Menu Items with realistic card layout */}
                    <div className="space-y-4 relative z-10">
                      {SKELETON_ITEMS.map((_, idx) => (
                        <div key={idx} className="flex gap-3 items-center">
                          
                          {/* Left: Image Skeleton */}
                          <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-gray-700 to-gray-800 shrink-0 animate-pulse" />

                          {/* Right: Content */}
                          <div className="flex-1 space-y-1.5">
                            
                            {/* Name + Price Row */}
                            <div className="flex justify-between items-center gap-2">
                              <div className={`h-4 ${NAME_WIDTHS[idx % 3]} bg-gray-700 rounded animate-pulse`} />
                              <div className="h-4 w-12 bg-gray-700 rounded shrink-0" />
                            </div>

                            {/* Description Line 1 */}
                            <div className={`h-3 ${DESC_WIDTHS[idx % 3]} bg-gray-800 rounded`} />

                            {/* Description Line 2 (shorter for realism) */}
                            <div className={`h-3 ${DESC_WIDTHS[(idx + 1) % 3]} bg-gray-800 rounded`} style={{ width: '75%' }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Progress Section */}
            <div className="flex-shrink-0 pb-8 text-center">
              {/* Status Text */}
              <motion.div
                key={statusText}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="mb-4"
              >
                <p className="text-lg md:text-xl text-[#EE4423] font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {statusText}
                </p>
              </motion.div>

              {/* Progress Bar - No competing animation */}
              <div className="w-full max-w-sm mx-auto bg-gray-800 rounded-full h-2 overflow-hidden mb-2">
                <div
                  style={{ width: `${progress}%` }}
                  className="h-full bg-gradient-to-r from-[#EE4423] to-[#d93a1c] transition-[width] duration-100 ease-linear"
                />
              </div>

              {/* Progress Percentage */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-sm text-gray-400"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {progress}% complete
              </motion.p>

              {/* Loading Dots */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="flex items-center justify-center gap-2 mt-4"
              >
                {[0, 1, 2].map(i => (
                  <motion.div
                    key={i}
                    animate={{ scale: [1, 1.5, 1], opacity: [0.3, 1, 0.3] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut', delay: i * 0.2 }}
                    className="w-2 h-2 bg-[#EE4423] rounded-full"
                  />
                ))}
              </motion.div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MenuPullingLoader;
